/**
 * AssessmentEndedBanner
 *
 * Shown on the Patient Overview (and the SSI proforma pages, read-only) when a
 * one-clinician SLaM / 3 Borough Lead chose "End assessment" at the end of the
 * Clinical Interview (wf.assessmentEndedAfterCI). It says who ended the
 * assessment and when, and that the later modules are locked. The reason and
 * next steps the clinician typed can be any length, so they are not printed
 * in the banner: a "View notes" control opens them in their own panel, which
 * scrolls when the text is longer than the panel. That panel names who
 * recorded the notes and when, in the same "Recorded on … by [name]" row the
 * Documents Dashboard uses for an approved section. "Resume assessment" is
 * offered to the people allowed to reverse the decision: the patient's Lead
 * clinician, an Org Admin, or Support.
 *
 * Resuming is deliberately a two-click action (button, then confirm dialog):
 * it unlocks clinical modules, so it must not happen on a mis-click, but it
 * is also not destructive (the CI report and the end record are kept), so a
 * typed confirmation would be disproportionate.
 *
 * What the Resume write does (see utils/assessmentEnd.js):
 *   - deletes workflowDecisions.ciEndDecision, which unlocks the SSI card and
 *     means re-finishing the CI asks the proceed / end question again;
 *   - stamps assessmentEnd.resumedAt / resumedBy / resumedByName;
 *   - appends a reportAuditHistory entry (assessmentResumedAfterCi).
 * Only root-level fields are touched, so Support and non-assigned Org Admins
 * pass the Firestore rules without a Cloud Function.
 *
 * Visual style matches SSIOverrideBanner / PadRedoArchiveBanner (amber,
 * left-aligned, ExclamationIcon) so it reads as the same family of
 * "something changed this patient's pathway" note.
 */

import React, { useState } from 'react';
import { doc, runTransaction } from 'firebase/firestore';
import * as Dialog from '@radix-ui/react-dialog';
import { ExclamationIcon, SearchIcon, XIcon } from '@heroicons/react/solid';
import { toast } from 'react-toastify';
import { db } from '../firebase';
import { formatDateTime } from '../utils/timestampUtils';
import { appendReportAuditEntry } from '../utils/reportAuditHelpers';
import {
  buildResumeAssessmentUpdates,
  ASSESSMENT_END_AUDIT_EVENTS,
} from '../utils/assessmentEnd';
import ModuleCompletionDialog, { CancelButton, STYLES } from './ModuleCompletionDialog';

/**
 * @param {Object} props
 * @param {boolean} props.isVisible
 * @param {string} props.patientId
 * @param {Object|null} props.assessmentEnd  Root `assessmentEnd` record
 *   ({ reason, nextSteps, endedAt, endedBy, endedByName, ... }).
 * @param {boolean} [props.canResume=false]  True for the Lead, Org Admin or
 *   Support; everyone else sees the note without the action.
 * @param {{uid: string, displayName?: string}|null} [props.currentUser]
 * @param {() => Promise|void} [props.onResumed]  Called after a successful
 *   resume so the page can re-fetch the patient.
 */
const AssessmentEndedBanner = ({
  isVisible = false,
  patientId,
  assessmentEnd = null,
  canResume = false,
  currentUser = null,
  onResumed,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  const endedByName = assessmentEnd?.endedByName || 'the Lead clinician';
  const endedAtLabel = formatDateTime(assessmentEnd?.endedAt, null);
  // Trimmed so a field saved as spaces counts as not recorded, and the
  // notes panel does not open onto a blank section.
  const reason = (assessmentEnd?.reason || '').trim();
  // Optional field; older records (before next steps were captured) have no
  // key at all, so that section is simply omitted rather than showing "none".
  const nextSteps = (assessmentEnd?.nextSteps || '').trim();
  // Nothing to open when neither field was recorded (including older records
  // written before next steps existed, and a missing end record).
  const hasNotes = Boolean(reason || nextSteps);

  const handleResume = async () => {
    if (isResuming || !patientId) return;
    setIsResuming(true);
    try {
      await runTransaction(db, async (transaction) => {
        transaction.update(
          doc(db, 'patients', patientId),
          buildResumeAssessmentUpdates({ user: currentUser }),
        );
      });
      appendReportAuditEntry(patientId, {
        module: 'ci',
        eventType: ASSESSMENT_END_AUDIT_EVENTS.RESUMED,
        isFirstOccurrence: false,
      });
      setIsConfirmOpen(false);
      toast.success('Assessment resumed. The Semi-Structured Interview is now available.');
      if (onResumed) await onResumed();
    } catch (error) {
      console.error('Failed to resume the assessment:', error);
      toast.error('Could not resume the assessment. Please try again.');
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <>
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isVisible ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
        aria-hidden={!isVisible}
      >
        <div className="bg-amber-50 rounded-2xl p-4 text-left" role="status">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-1">
              <ExclamationIcon className="h-5 w-5 text-amber-600" aria-hidden="true" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-md font-medium text-amber-800">
                Assessment ended after the Clinical Interview
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                {/* One sentence of fact, one of consequence. The module
                    cards below already show which modules are locked, so
                    they are not listed again here. */}
                <p className="mb-2">
                  <strong>{endedByName}</strong> ended this assessment
                  {endedAtLabel ? <> on {endedAtLabel}</> : null}. The remaining
                  modules are locked{canResume ? ' until the assessment is resumed.' : '.'}
                </p>
                {/* One filled button (Resume, the action) and at most one text
                    link (View notes, for reading). Notes stay out of this
                    amber box so a long reason cannot stretch or clip it. */}
                {(hasNotes || canResume) && (
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    {canResume && (
                      <button
                        type="button"
                        onClick={() => setIsConfirmOpen(true)}
                        className="inline-flex items-center min-h-[44px] py-2 px-4 font-semibold rounded-md bg-primary text-white hover:bg-primary/90 whitespace-nowrap"
                      >
                        Resume assessment
                      </button>
                    )}
                    {hasNotes && (
                      <button
                        type="button"
                        onClick={() => setIsNotesOpen(true)}
                        className="inline-flex items-center gap-1.5 min-h-[44px] text-sm font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                      >
                        <SearchIcon className="h-4 w-4" aria-hidden="true" />
                        View notes
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {hasNotes && (
        <Dialog.Root open={isNotesOpen} onOpenChange={setIsNotesOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
            <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 z-50 w-[560px] max-w-[90vw] max-h-[70vh] -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-xl flex flex-col">
              <div className="flex justify-between items-start gap-4 shrink-0 mb-4">
                <Dialog.Title className="text-xl font-bold text-gray-900">
                  Notes recorded
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] -mt-2 -mr-2 rounded-full hover:bg-gray-100"
                  >
                    <XIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
                  </button>
                </Dialog.Close>
              </div>
              <Dialog.Description className="sr-only">
                The reason and any next steps recorded when the assessment was ended, with who recorded them and when.
              </Dialog.Description>
              {/* Same attribution row as an approved section on the Documents
                  Dashboard: italic "on <date and time> by" plus the clinician's
                  name in a pill. The date is omitted until it has resolved, so
                  we never show "on Unknown date". */}
              <div className="flex items-center gap-2 mb-4 shrink-0">
                <p className="text-sm italic text-gray-700">
                  {endedAtLabel ? `Recorded on ${endedAtLabel} by` : 'Recorded by'}
                </p>
                <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                  <span>{endedByName}</span>
                </div>
              </div>
              {/* The panel scrolls once the notes outgrow it; short notes
                  sit in full with no scrollbar. */}
              <div className="overflow-y-auto flex-1 min-h-0 space-y-5 text-sm text-gray-800">
                {reason && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Reason</h3>
                    <p className="whitespace-pre-wrap break-words">{reason}</p>
                  </div>
                )}
                {nextSteps && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Next steps</h3>
                    <p className="whitespace-pre-wrap break-words">{nextSteps}</p>
                  </div>
                )}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {canResume && (
        <ModuleCompletionDialog
          open={isConfirmOpen}
          onOpenChange={(next) => {
            if (!isResuming) setIsConfirmOpen(next);
          }}
          title="Resume assessment"
          description="The Semi-Structured Interview becomes available and the assessment continues from where it stopped."
          width={560}
          actions={
            <>
              <CancelButton onClick={() => setIsConfirmOpen(false)} label="Cancel" />
              {/* Plain button rather than PrimaryActionButton (AlertDialog.Action)
                  so the dialog stays open and shows progress while the write
                  is in flight; it is closed explicitly on success. */}
              <button
                type="button"
                disabled={isResuming}
                className={`flex items-center px-4 py-2 mb-2 font-semibold rounded-md transition-all duration-300 ${
                  isResuming ? STYLES.disabled : STYLES.primary
                }`}
                onClick={handleResume}
              >
                {isResuming ? 'Resuming…' : 'Resume assessment'}
              </button>
            </>
          }
        >
          <p className="mb-6 text-sm text-gray-700">
            The Clinical Interview report and the record of why the assessment
            ended are kept. The Assessment Conclusion paragraph is left out of
            the report while the assessment is in progress.
          </p>
        </ModuleCompletionDialog>
      )}
    </>
  );
};

export default AssessmentEndedBanner;
